import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { broadcastEntityChange } from "@/lib/useEntityRefresh";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, UserPlus, Globe, Copy, ExternalLink, KeyRound, FileSpreadsheet, Search, X, Copy as CopyIcon, Bell, Link2, Link2Off, FileSignature } from "lucide-react";
import { SendWhatsAppButton } from "@/components/SendWhatsAppButton";
import NotifPrefsPanel from "@/components/NotifPrefsPanel";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { ImportFactusolDialog } from "@/components/ImportFactusolDialog";
import DuplicadosDialog from "@/components/DuplicadosDialog";
import { toast } from "sonner";

const empty = {
  nombre: "", cif: "", tipo: "Empresa", forma_pago: "30 días",
  tipo_facturacion: "factura_directa",
  logo_url: "", direccion_fiscal: "", email_contacto: "", telefono: "",
  persona_contacto: "", notas: "", contactos: [],
  comercial_id: "",
  portal_config: {
    habilitado: false,
    ver_lista_peticiones: true,
    ver_detalle_peticion: true,
    ver_fotos_montaje: false,
    ver_pdfs_incidencia: false,
    ver_adjuntos: false,
    ver_economico: false,
    ver_facturas: true,
    puede_aprobar_aaff: false,
    puede_aprobar_presupuesto: false,
  },
};

const ROLES_CONTACTO = ["Comercial", "AAFF / Diseño", "Envíos", "Administración", "Jefe", "Otro"];

function nuevoContacto() {
  return { id: Math.random().toString(36).slice(2, 10), nombre: "", rol: "Comercial", email: "", telefono: "", notas: "", principal: false, fecha_nacimiento: "" };
}

export default function ClientesPage() {
  const [items, setItems] = useState([]);
  const [comerciales, setComerciales] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [duplicadosOpen, setDuplicadosOpen] = useState(false);
  const [notifPrefsClient, setNotifPrefsClient] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("todos");
  const load = () => api.get("/clientes").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setComerciales(
      (r.data || []).filter((u) => u.active && (u.roles || []).some((x) => ["admin", "comercial"].includes(x)))
    )).catch(() => {});
  }, []);

  const filtered = items.filter((c) => {
    if (filterTipo !== "todos" && (c.tipo || "—") !== filterTipo) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [
      c.nombre, c.cif, c.email_contacto, c.telefono, c.persona_contacto,
      c.direccion_fiscal, c.codigo_factusol,
      ...(c.contactos || []).flatMap((ct) => [ct.nombre, ct.email, ct.telefono]),
    ].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, contactos: c.contactos || [], portal_config: { ...empty.portal_config, ...(c.portal_config || {}) } }); setOpen(true); };

  const setPortal = (k, v) => setForm((f) => ({ ...f, portal_config: { ...(f.portal_config || empty.portal_config), [k]: v } }));

  const [magicLink, setMagicLink] = useState(null); // {url, mensaje, telefono}

  const [linkFdLoading, setLinkFdLoading] = useState(false);
  const [linkFdBulkResult, setLinkFdBulkResult] = useState(null);

  const linkFdCliente = async (c) => {
    if (linkFdLoading) return;
    setLinkFdLoading(true);
    try {
      const { data } = await api.post(`/clientes/${c.id}/link-fd`);
      if (data.ok && data.fd_id) {
        toast.success(`✅ ${c.nombre} enlazado con FacturaDirecta (${data.via})`);
        load();
      } else {
        toast.error(data.mensaje || "No se encontró contacto en FD ni en caché");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enlazando con FD");
    } finally {
      setLinkFdLoading(false);
    }
  };

  const linkFdBulk = async () => {
    if (linkFdLoading) return;
    if (!confirm("Se recorrerán TODOS los clientes sin fd_id y se intentará enlazarlos con FacturaDirecta por CIF (o por nombre en la caché de facturas). ¿Continuar?")) return;
    setLinkFdLoading(true);
    try {
      const { data } = await api.post("/clientes/link-fd-bulk");
      setLinkFdBulkResult(data);
      toast.success(`🔗 Enlazados: ${data.enlazados} · No encontrados: ${data.no_encontrados} · Sin CIF: ${data.sin_cif_ni_match}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en el enlace masivo");
    } finally {
      setLinkFdLoading(false);
    }
  };

  const generarPortalLink = async () => {
    if (!editing?.id) return toast.error("Guarda el cliente primero");
    if (!form.portal_config?.habilitado) return toast.error("Activa el portal primero");
    try {
      // Guardar config actual antes de generar
      await api.patch(`/clientes/${editing.id}`, { portal_config: form.portal_config });
      const { data } = await api.post(`/clientes/${editing.id}/portal-link`, { dias: 0 });
      const fullUrl = `${window.location.origin}${data.url}`;
      const nombre = (form.contactos?.[0]?.nombre || form.persona_contacto || "").split(" ")[0] || "";
      const saludo = nombre ? `Hola ${nombre} 👋` : "Hola 👋";
      const mensaje = `${saludo}\n\nAccede a tu portal de RotulaciónDNF aquí 👇\n${fullUrl}\n\n(Enlace permanente: guárdalo en favoritos)`;
      const tel = (form?.telefono || "").replace(/[^0-9+]/g, "").replace(/^\+/, "");
      setMagicLink({ url: fullUrl, mensaje, telefono: tel });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando enlace");
    }
  };

  const copiarTexto = async (text, etiqueta = "Texto") => {
    // 1) Try modern API
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(`${etiqueta} copiado ✓`);
        return true;
      }
    } catch (_) { /* falls through */ }
    // 2) Fallback execCommand (iOS Safari y otros)
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast.success(`${etiqueta} copiado ✓`);
        return true;
      }
    } catch (_) { /* falls through */ }
    toast.info("Copia manual: el texto está seleccionado");
    return false;
  };

  const pedirResena = async (c) => {
    try {
      const { data: emp } = await api.get("/presupuestos-config/empresa").catch(() => ({ data: {} }));
      const url = emp?.google_review_url || "https://share.google/0fBLPoVKhiW4Z154R";
      const nombre = (c?.contactos?.[0]?.nombre || c?.persona_contacto || "").split(" ")[0] || "";
      const saludo = nombre ? `Hola ${nombre} 👋` : "Hola 👋";
      const msg = `${saludo}\n\nMuchas gracias por confiar en *RotulaciónDNF* para tu proyecto. Si te ha gustado el resultado, nos ayudaría muchísimo una reseña en Google ⭐\n\n${url}\n\n¡Un abrazo!`;
      await copiarTexto(url, "Enlace");
      // Si el cliente tiene teléfono, prerellenarlo (formato wa.me/<num>)
      const tel = (c?.telefono || "").replace(/[^0-9+]/g, "").replace(/^\+/, "");
      const target = tel ? `https://wa.me/${tel}?text=${encodeURIComponent(msg)}` : `https://web.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
      window.open(target, "_blank");
    } catch (e) {
      toast.error("Error generando enlace de reseña");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Limpiar contactos vacíos
    payload.contactos = (payload.contactos || []).filter((c) => c.nombre?.trim());
    Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
    if (editing) await api.patch(`/clientes/${editing.id}`, payload);
    else await api.post("/clientes", payload);
    broadcastEntityChange("cliente", { action: editing ? "update" : "create" });
    setOpen(false); load();
  };

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Clientes"
        subtitle="Información fiscal y comercial de tus clientes"
        action={
          <div className="flex gap-2">
            <Button onClick={linkFdBulk} disabled={linkFdLoading}
                    variant="outline"
                    className="bg-transparent border-blue-700/60 text-blue-200 hover:bg-blue-950/40"
                    data-testid="link-fd-bulk-btn"
                    title="Buscar en FacturaDirecta por CIF y enlazar automáticamente todos los clientes sin fd_id">
              <Link2 className="w-4 h-4 mr-2" />
              {linkFdLoading ? "Enlazando…" : "Auto-enlazar FD"}
            </Button>
            <Button onClick={() => setDuplicadosOpen(true)}
                    variant="outline"
                    className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                    data-testid="buscar-duplicados-btn-clientes">
              <CopyIcon className="w-4 h-4 mr-2" /> Buscar duplicados
            </Button>
            <Button onClick={() => setImportOpen(true)} variant="outline"
                    className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                    data-testid="import-factusol-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Importar Factusol
            </Button>
            <Button onClick={openCreate} data-testid="new-cliente-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Nuevo cliente
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, CIF, email, teléfono, ciudad, código…"
            className="bg-zinc-900 border-zinc-800 pl-9 pr-9 text-zinc-100 placeholder:text-zinc-500"
            data-testid="clientes-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-200"
              data-testid="clientes-search-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
          {["todos", "Empresa", "Particular", "Agencia"].map((t) => (
            <button key={t}
              onClick={() => setFilterTipo(t)}
              data-testid={`clientes-filter-${t}`}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                filterTipo === t ? "bg-amber-500 text-zinc-900 font-semibold"
                                  : "text-zinc-400 hover:text-zinc-100"}`}>
              {t === "todos" ? "Todos" : t}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-zinc-500">
          <span className="font-mono text-zinc-300">{filtered.length}</span> de <span className="font-mono">{items.length}</span> clientes
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`cliente-card-${c.id}`}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/40 transition-colors">
            <div className="flex items-start gap-3 mb-3">
              {c.logo_url ? (
                <img src={c.logo_url} alt={c.nombre} className="w-12 h-12 rounded-lg object-contain bg-white p-1" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-zinc-500" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-zinc-100 truncate">{c.nombre}</div>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-zinc-500 font-mono">{c.cif || "Sin CIF"}</div>
                  {c.fd_id ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300/80 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5"
                          data-testid={`cliente-fd-linked-${c.id}`}
                          title={`FD ID: ${c.fd_id}`}>
                      <Link2 className="w-2.5 h-2.5" /> FD
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5"
                          data-testid={`cliente-fd-unlinked-${c.id}`}
                          title="Sin fd_id — depende del fallback por nombre. Puede fallar en el portal cliente.">
                      <Link2Off className="w-2.5 h-2.5" /> Sin FD
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-1 text-xs text-zinc-400">
              {c.tipo && <div><span className="text-zinc-500">Tipo:</span> {c.tipo}</div>}
              {c.forma_pago && <div><span className="text-zinc-500">Pago:</span> {c.forma_pago}</div>}
              {c.persona_contacto && <div><span className="text-zinc-500">Contacto:</span> {c.persona_contacto}</div>}
              {c.telefono && (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">Tel:</span>
                  <span>{c.telefono}</span>
                  <SendWhatsAppButton
                    to={c.telefono}
                    nombreDestinatario={c.nombre}
                    size="xs"
                    variant="ghost"
                    iconOnly
                    testId={`send-wa-cliente-${c.id}`}
                  />
                </div>
              )}
              {c.email_contacto && <div className="truncate"><span className="text-zinc-500">Email:</span> {c.email_contacto}</div>}
              {c.direccion_fiscal && <div className="truncate"><span className="text-zinc-500">Dir.:</span> {c.direccion_fiscal}</div>}
              {(c.contactos || []).length > 0 && (
                <div className="flex items-center gap-1 text-amber-300/80 mt-1">
                  <UserPlus className="w-3 h-3" /> {(c.contactos || []).length} contacto{(c.contactos || []).length > 1 ? "s" : ""}
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-800">
              <Button size="sm" variant="ghost" onClick={() => openEdit(c)}
                className="text-zinc-400 hover:text-amber-300 h-7 px-2">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
              {!c.fd_id && (
                <Button size="sm" variant="ghost" onClick={() => linkFdCliente(c)}
                  disabled={linkFdLoading}
                  data-testid={`btn-link-fd-${c.id}`}
                  title="Buscar contacto en FacturaDirecta por CIF y enlazar"
                  className="text-blue-300 hover:text-blue-200 h-7 px-2">
                  <Link2 className="w-3.5 h-3.5 mr-1" /> Enlazar FD
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => pedirResena(c)}
                data-testid={`btn-pedir-resena-${c.id}`}
                title="Pedir reseña Google"
                className="text-zinc-400 hover:text-amber-300 h-7 px-2">
                ⭐ Reseña
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setNotifPrefsClient(c)}
                data-testid={`btn-notif-prefs-${c.id}`}
                title="Notificaciones del cliente (canales + resumen diario)"
                className="text-zinc-400 hover:text-amber-300 h-7 px-2">
                <Bell className="w-3.5 h-3.5 mr-1" /> Notif
              </Button>
              <Button size="sm" variant="ghost"
                onClick={async () => { if (confirm(`¿Eliminar ${c.nombre}?`)) { await api.delete(`/clientes/${c.id}`); load(); } }}
                className="text-zinc-500 hover:text-red-400 h-7 px-2">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-zinc-500">
            {items.length === 0 ? "Sin clientes todavía." : `Ningún cliente coincide con "${search}".`}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nombre *</Label>
                <Input required value={form.nombre} data-testid="form-cliente-nombre"
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>CIF</Label>
                <Input value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {["Empresa", "Particular", "Agencia", "Otro"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Forma de pago</Label>
                <Select value={form.forma_pago} onValueChange={(v) => setForm({ ...form, forma_pago: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {["Contado", "Transferencia", "15 días", "30 días", "60 días", "90 días"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Persona de contacto</Label>
                <Input value={form.persona_contacto} onChange={(e) => setForm({ ...form, persona_contacto: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Email</Label>
                <Input type="email" value={form.email_contacto} onChange={(e) => setForm({ ...form, email_contacto: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2 bg-blue-500/5 border border-blue-500/30 rounded-lg p-3">
                <Label className="text-xs text-blue-200">📦 Modelo de facturación</Label>
                <Select value={form.tipo_facturacion || "factura_directa"}
                  onValueChange={(v) => setForm({ ...form, tipo_facturacion: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="form-cliente-tipo-facturacion">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="factura_directa">Factura directa (cliente final) — al entregar petición se emite factura</SelectItem>
                    <SelectItem value="albaran_mensual">Albarán mensual (agencia) — albarán por entrega, factura única el día 23 del mes</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Decor Galva, MPO, Proyectos, Implanta, Diagrafos → usar "Albarán mensual"
                </div>
              </div>
              <div className="col-span-2 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
                <Label className="text-xs text-amber-200">👤 Comercial responsable</Label>
                <Select value={form.comercial_id || "__NONE__"}
                  onValueChange={(v) => setForm({ ...form, comercial_id: v === "__NONE__" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="form-cliente-comercial">
                    <SelectValue placeholder="— Asignar comercial —" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-64">
                    <SelectItem value="__NONE__">— Sin asignar —</SelectItem>
                    {comerciales.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Recibe avisos de cumple de los contactos de este cliente y otras notificaciones relacionadas.
                </div>
              </div>
              <div><Label>Logo del cliente</Label>
                <div className="flex items-center gap-2">
                  {form.logo_url && (
                    <img src={form.logo_url} alt="logo" className="w-10 h-10 rounded bg-white p-0.5 object-contain border border-zinc-700" />
                  )}
                  <Input value={form.logo_url} placeholder="https://..." onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="bg-zinc-900 border-zinc-800 flex-1" />
                  <label className="cursor-pointer bg-amber-500 hover:bg-amber-400 text-zinc-900 text-xs font-semibold px-3 py-2 rounded inline-flex items-center gap-1" data-testid="cliente-logo-upload">
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          toast.info("Subiendo logo...");
                          const { secure_url } = await uploadToCloudinary(file, "dnf/clientes/logos", "image");
                          setForm({ ...form, logo_url: secure_url });
                          toast.success("Logo subido");
                        } catch (err) {
                          toast.error("Error al subir: " + (err.message || err));
                        } finally {
                          e.target.value = "";
                        }
                      }} />
                    Subir
                  </label>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">Se mostrará en su portal de cliente. Recomendado: PNG con fondo transparente.</div>
              </div>
              <div className="col-span-2"><Label>Dirección fiscal</Label>
                <AddressAutocomplete value={form.direccion_fiscal}
                  onChange={(v) => setForm({ ...form, direccion_fiscal: v })}
                  onSelect={(loc) => setForm({ ...form, direccion_fiscal: loc.direccion })}
                  placeholder="ej. Avda. de la Industria 25, Madrid"
                  data-testid="form-cliente-direccion"
                  className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2"><Label>Notas</Label>
                <Textarea value={form.notas} rows={2} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            </div>

            {/* === CONTACTOS === */}
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-amber-300 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" /> Contactos del cliente
                </Label>
                <Button type="button" size="sm" variant="ghost"
                  onClick={() => setForm({ ...form, contactos: [...(form.contactos || []), nuevoContacto()] })}
                  data-testid="add-contacto-btn"
                  className="text-amber-300 hover:text-amber-200 h-7 px-2 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Añadir contacto
                </Button>
              </div>
              {(!form.contactos || form.contactos.length === 0) && (
                <div className="text-xs text-zinc-500 italic">Sin contactos. Añade personas concretas: quién manda peticiones, quién envía AAFF, quién hace envíos, etc.</div>
              )}
              <div className="space-y-2">
                {(form.contactos || []).map((ct, idx) => (
                  <div key={ct.id} className="grid grid-cols-[1.3fr_1fr_1.5fr_1fr_0.9fr_auto] gap-2 items-end">
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Nombre</Label>}
                      <Input placeholder="ej. Floren García" value={ct.nombre}
                        data-testid={`contacto-nombre-${idx}`}
                        onChange={(ev) => {
                          const arr = [...form.contactos]; arr[idx] = { ...arr[idx], nombre: ev.target.value };
                          setForm({ ...form, contactos: arr });
                        }} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                    </div>
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Rol</Label>}
                      <Select value={ct.rol || "Comercial"} onValueChange={(v) => {
                        const arr = [...form.contactos]; arr[idx] = { ...arr[idx], rol: v };
                        setForm({ ...form, contactos: arr });
                      }}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-800 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                          {ROLES_CONTACTO.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Email</Label>}
                      <Input type="email" placeholder="email@..." value={ct.email || ""}
                        onChange={(ev) => {
                          const arr = [...form.contactos]; arr[idx] = { ...arr[idx], email: ev.target.value };
                          setForm({ ...form, contactos: arr });
                        }} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                    </div>
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Teléfono</Label>}
                      <Input placeholder="000 000 000" value={ct.telefono || ""}
                        onChange={(ev) => {
                          const arr = [...form.contactos]; arr[idx] = { ...arr[idx], telefono: ev.target.value };
                          setForm({ ...form, contactos: arr });
                        }} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                    </div>
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-pink-300">🎂 Cumple</Label>}
                      <Input type="date" value={ct.fecha_nacimiento || ""}
                        title="Cumpleaños — el comercial recibirá aviso"
                        onChange={(ev) => {
                          const arr = [...form.contactos]; arr[idx] = { ...arr[idx], fecha_nacimiento: ev.target.value };
                          setForm({ ...form, contactos: arr });
                        }} className="bg-zinc-900 border-zinc-800 h-8 text-xs"
                        data-testid={`contacto-cumple-${idx}`} />
                    </div>
                    <Button type="button" size="sm" variant="ghost"
                      onClick={() => setForm({ ...form, contactos: form.contactos.filter((_, i) => i !== idx) })}
                      className="text-zinc-500 hover:text-red-400 h-8 w-8 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* PORTAL CLIENTE */}
            <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="cliente-portal-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-zinc-200">Portal del cliente</h3>
                  {form.portal_config?.habilitado && (
                    <span className="text-[10px] uppercase tracking-wider bg-emerald-500/30 text-emerald-200 px-1.5 py-0.5 rounded">activo</span>
                  )}
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.portal_config?.habilitado}
                    onChange={(e) => setPortal("habilitado", e.target.checked)}
                    data-testid="portal-habilitado-toggle"
                    className="w-4 h-4 accent-amber-500"
                  />
                  Habilitar acceso
                </label>
              </div>

              {form.portal_config?.habilitado && (
                <>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <PortalToggle label="Ver lista de peticiones" k="ver_lista_peticiones" form={form} setPortal={setPortal} />
                    <PortalToggle label="Ver detalle de petición" k="ver_detalle_peticion" form={form} setPortal={setPortal} />
                    <PortalToggle label="Ver fotos de montaje" k="ver_fotos_montaje" form={form} setPortal={setPortal} />
                    <PortalToggle label="Descargar PDF incidencias" k="ver_pdfs_incidencia" form={form} setPortal={setPortal} />
                    <PortalToggle label="Ver adjuntos (AAFF, planos)" k="ver_adjuntos" form={form} setPortal={setPortal} />
                    <PortalToggle label="Ver datos económicos" k="ver_economico" form={form} setPortal={setPortal} />
                    <PortalToggle label="Ver facturas (FacturaDirecta)" k="ver_facturas" form={form} setPortal={setPortal} />
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-2 italic">
                    💡 Tip para agencias: oculta fotos/adjuntos. Tip para clientes finales: activa fotos para mejor experiencia.
                  </div>
                  {/* Iter 81 — Opt-out global de geolocalización para fotos del montador (pedido Floren Decor Galva) */}
                  <label className="flex items-center gap-2 text-xs cursor-pointer mt-3 pt-3 border-t border-zinc-800 hover:text-amber-200 transition-colors">
                    <input
                      type="checkbox"
                      checked={form.exigir_geolocalizacion !== false}
                      onChange={(e) => setForm({ ...form, exigir_geolocalizacion: e.target.checked })}
                      data-testid="cliente-exigir-geo-toggle"
                      className="w-3.5 h-3.5 accent-amber-500"
                    />
                    <span>Exigir geolocalización en fotos del montador (estampar coordenadas)</span>
                  </label>
                  <div className="text-[10px] text-zinc-500 italic ml-6">
                    Desmárcalo para clientes que NO quieren ver coordenadas estampadas en las fotos (las nuevas peticiones heredarán este ajuste).
                  </div>
                  {editing?.id && (
                    <div className="mt-3 flex items-center gap-2">
                      <Button type="button" onClick={generarPortalLink}
                        data-testid="generar-portal-link-btn"
                        className="bg-amber-500 hover:bg-amber-400 text-zinc-900 h-8 text-xs font-semibold">
                        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Generar enlace mágico (30 días)
                      </Button>
                      <span className="text-[11px] text-zinc-500">→ abre diálogo con link copiable + WhatsApp</span>
                    </div>
                  )}
                  {!editing?.id && (
                    <div className="text-[11px] text-amber-300 mt-2">⚠ Guarda el cliente primero para generar el enlace.</div>
                  )}
                </>
              )}
            </div>

              {/* Jun-2026 · Datos de contratación exigidos para facturar (DIAGRAFOS…) */}
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-1.5" data-testid="cliente-contratacion-config">
                <h3 className="text-sm font-semibold text-zinc-200 flex items-center gap-2"><FileSignature className="w-4 h-4 text-amber-400" /> Facturación · datos de contratación</h3>
                <label className="flex items-center gap-2 text-xs cursor-pointer hover:text-amber-200 transition-colors">
                  <input type="checkbox" checked={!!form.contratacion_config?.activo} data-testid="cliente-contratacion-activo" className="w-3.5 h-3.5 accent-amber-500"
                    onChange={(e) => setForm({ ...form, contratacion_config: { exigir_referencia: true, exigir_contratacion: true, exigir_pedido: true, exigir_importe: false, ...(form.contratacion_config || {}), activo: e.target.checked } })} />
                  <span>Exige datos de contratación para facturar (nº contratación / pedido del cliente)</span>
                </label>
                {form.contratacion_config?.activo && (
                  <div className="ml-6 grid grid-cols-2 gap-1 text-[11px] text-zinc-300">
                    {[["exigir_referencia", "Referencia cliente obligatoria"], ["exigir_contratacion", "Nº contratación obligatorio"], ["exigir_pedido", "Nº pedido obligatorio"], ["exigir_importe", "Importe contratado obligatorio"]].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={form.contratacion_config?.[k] !== false && (k !== "exigir_importe" || form.contratacion_config?.[k] === true)} data-testid={`cliente-contratacion-${k}`} className="accent-amber-500"
                        onChange={(e) => setForm({ ...form, contratacion_config: { ...form.contratacion_config, [k]: e.target.checked } })} /> {l}</label>
                    ))}
                    <div className="col-span-2 text-[10px] text-zinc-500 italic">Las hojas de contratación recibidas por correo generan propuestas (nunca peticiones nuevas) que se confirman en «Contrataciones cliente». El concepto FD llevará «Ref. · Contratación · Pedido».</div>
                  </div>
                )}
              </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
              <Button type="submit" data-testid="form-cliente-submit" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MAGIC LINK DIALOG — robusto, copy manual + botones que sí funcionan */}
      <Dialog open={!!magicLink} onOpenChange={(o) => { if (!o) setMagicLink(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-400" />Enlace mágico generado (válido 30 días)</DialogTitle>
          </DialogHeader>
          {magicLink && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400">Enlace de acceso</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={magicLink.url} readOnly
                    onFocus={(e) => e.target.select()}
                    data-testid="magic-link-input"
                    className="bg-zinc-950 border-zinc-700 font-mono text-xs select-all" />
                  <Button type="button" size="sm" onClick={() => copiarTexto(magicLink.url, "Enlace")}
                    data-testid="magic-link-copy-url"
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold whitespace-nowrap">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Mensaje sugerido (puedes editarlo antes de enviar)</Label>
                <Textarea value={magicLink.mensaje}
                  onChange={(e) => setMagicLink({ ...magicLink, mensaje: e.target.value })}
                  rows={5}
                  data-testid="magic-link-mensaje"
                  className="bg-zinc-950 border-zinc-700 text-xs mt-1 font-mono" />
                <Button type="button" size="sm" variant="ghost"
                  onClick={() => copiarTexto(magicLink.mensaje, "Mensaje completo")}
                  data-testid="magic-link-copy-mensaje"
                  className="text-amber-300 hover:text-amber-200 mt-1 h-7 text-xs">
                  <Copy className="w-3 h-3 mr-1" /> Copiar mensaje completo
                </Button>
              </div>
              {magicLink.telefono && (
                <div className="text-[11px] text-emerald-300">📞 Cliente con teléfono: <span className="font-mono">+{magicLink.telefono}</span></div>
              )}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-800">
                <Button type="button"
                  onClick={() => {
                    const tel = (magicLink.telefono || "").trim();
                    const url = tel
                      ? `https://wa.me/${tel}?text=${encodeURIComponent(magicLink.mensaje)}`
                      : `https://web.whatsapp.com/send?text=${encodeURIComponent(magicLink.mensaje)}`;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  data-testid="magic-link-whatsapp"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
                  💬 Abrir WhatsApp{magicLink.telefono ? " (cliente)" : ""}
                </Button>
                <Button type="button" variant="outline"
                  onClick={() => {
                    window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(magicLink.mensaje)}`, "_blank", "noopener,noreferrer");
                  }}
                  data-testid="magic-link-whatsapp-web"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                  🌐 WhatsApp Web
                </Button>
              </div>
              <div className="text-[10px] text-zinc-500">
                💡 Si "Copiar" falla en tu navegador, selecciona el texto y usa Cmd/Ctrl+C manualmente.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setMagicLink(null)} variant="outline" className="bg-transparent border-zinc-700">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportFactusolDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <DuplicadosDialog
        open={duplicadosOpen}
        onOpenChange={setDuplicadosOpen}
        entidad="clientes"
        fetchPath="/clientes/duplicados-candidatos"
        deletePath="/clientes"
        onEdit={(it) => {
          const full = items.find((x) => x.id === it.id);
          if (full) { setDuplicadosOpen(false); openEdit(full); }
          else toast.info("Refresca la lista, el cliente no está cargado.");
        }}
        onAfterChange={load}
        renderItem={(it) => (
          <div className="text-sm">
            <div className="font-semibold text-zinc-100">{it.nombre}</div>
            <div className="text-xs text-zinc-400 mt-0.5">{it.cif || "Sin CIF"}</div>
            {it.email_contacto && <div className="text-[11px] text-zinc-500 truncate">{it.email_contacto}</div>}
            {it.telefono && <div className="text-[11px] text-zinc-500">{it.telefono}</div>}
          </div>
        )}
      />

      {/* Dialog Notificaciones por cliente */}
      <Dialog open={!!notifPrefsClient} onOpenChange={(o) => !o && setNotifPrefsClient(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {notifPrefsClient && (
            <NotifPrefsPanel
              clienteId={notifPrefsClient.id}
              mode="admin"
              onClose={() => setNotifPrefsClient(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PortalToggle({ label, k, form, setPortal }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer hover:text-amber-200 transition-colors">
      <input
        type="checkbox"
        checked={!!form.portal_config?.[k]}
        onChange={(e) => setPortal(k, e.target.checked)}
        data-testid={`portal-${k}-toggle`}
        className="w-3.5 h-3.5 accent-amber-500"
      />
      <span>{label}</span>
    </label>
  );
}
